import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { landingApi, type LandingPromo } from "@/lib/api";
import { isExternalHref } from "@/lib/landing-hero";
import { DEFAULT_PROMO, cachedPromo, rememberPromo } from "@/lib/landing-promo";
import { cn } from "@/lib/utils";

/** How long to wait for the API before showing the built-in banner, as the hero does. */
const SETTLE_MS = 1500;

/** Cards per row by count, as literal classes so Tailwind sees them. */
const ITEM_COLS: Record<number, string> = {
  1: "grid-cols-1 max-w-[11rem]",
  2: "grid-cols-2 max-w-sm",
  3: "grid-cols-3",
  4: "grid-cols-2 sm:grid-cols-4",
};

/**
 * The "Lebih lengkap," banner under the home page hero: what a desk comes
 * with on a sage half, the desk and a red sticker on a cream one. Authored in the admin under "Landing page"; a campaign page passes its
 * own.
 *
 * It sits below the fold, so unlike the hero it simply isn't rendered until
 * the authored copy (or the cached copy, or the built-in one) is in hand.
 */
export function PromoBanner({ authored }: { authored?: LandingPromo } = {}) {
  const [promo, setPromo] = useState<LandingPromo | null>(authored ?? null);

  useEffect(() => {
    if (authored) {
      setPromo(authored);
      return;
    }
    let live = true;
    const cached = cachedPromo();
    if (cached) setPromo(cached);
    const fallback = setTimeout(() => {
      if (live) setPromo((p) => p ?? DEFAULT_PROMO);
    }, SETTLE_MS);
    landingApi
      .promo()
      .then((p) => {
        if (!live) return;
        setPromo(p);
        rememberPromo(p);
      })
      .catch(() => {
        if (live) setPromo((p) => p ?? DEFAULT_PROMO);
      })
      .finally(() => clearTimeout(fallback));
    return () => {
      live = false;
      clearTimeout(fallback);
    };
  }, [authored]);

  // A campaign section hides itself through its own switch, so `hidden` only
  // counts for the home page's banner.
  if (!promo || (!authored && promo.hidden) || promo.items.length === 0) return null;
  return <PromoCard promo={promo} />;
}

export function PromoCard({ promo }: { promo: LandingPromo }) {
  const n = promo.items.length;
  return (
    <section
      aria-label={[promo.title, promo.accent].filter(Boolean).join(" ")}
      className="mx-auto max-w-7xl px-5 pt-12 sm:px-8 sm:pt-16"
    >
      <div className="grid overflow-hidden rounded-3xl lg:grid-cols-2">
        <div className="bg-promo px-6 py-8 sm:px-10 sm:py-12">
          {promo.label && (
            <span className="inline-block rounded-full bg-brand-lime/85 px-4 py-1.5 text-xs font-medium uppercase tracking-wide text-brand-darker sm:text-sm">
              {promo.label}
            </span>
          )}
          <h2 className="mt-5 text-4xl font-medium leading-[1.05] tracking-tight sm:text-5xl">
            {promo.title && <span className="block text-white">{promo.title}</span>}
            {promo.accent && <span className="block text-brand-lime">{promo.accent}</span>}
          </h2>
          {promo.body && <p className="mt-4 max-w-xl text-base text-brand-lime sm:text-lg lg:text-xl">{promo.body}</p>}

          <ul className={cn("mt-7 grid gap-3", ITEM_COLS[n])}>
            {promo.items.map((item, i) => (
              <li
                key={i}
                className="relative flex flex-col rounded-2xl bg-white p-3 shadow-[0_4px_0_rgb(0_0_0/0.25)] sm:p-4"
              >
                <div className="flex h-14 items-center justify-center sm:h-20">
                  <img src={item.image} alt="" loading="lazy" className="max-h-full max-w-full object-contain" />
                </div>
                <p className="mt-3 whitespace-pre-line text-xs font-medium leading-snug text-promo-ink sm:text-sm">
                  {item.title}
                </p>
                {item.subtitle && (
                  <p className="mt-1.5 text-[11px] leading-snug text-promo sm:text-xs">{item.subtitle}</p>
                )}
                {i < n - 1 && (
                  <span
                    aria-hidden
                    className={cn(
                      "absolute -right-1.5 top-3 z-10 flex size-5 translate-x-1/2 items-center justify-center rounded-full bg-brand-lime text-brand-darker sm:size-6",
                      // Four cards wrap two by two on a phone: no "+" off the row's end.
                      n === 4 && i === 1 && "hidden sm:flex",
                    )}
                  >
                    <Plus className="size-3 stroke-[3] sm:size-3.5" />
                  </span>
                )}
              </li>
            ))}
          </ul>

          {promo.ctaLabel && promo.ctaHref && (
            <Button asChild variant="white" className="mt-8">
              {isExternalHref(promo.ctaHref) ? (
                <a href={promo.ctaHref} target="_blank" rel="noopener noreferrer">
                  {promo.ctaLabel}
                  <ArrowRight />
                </a>
              ) : (
                <Link to={promo.ctaHref}>
                  {promo.ctaLabel}
                  <ArrowRight />
                </Link>
              )}
            </Button>
          )}
        </div>

        <div className="relative order-first flex items-end justify-center bg-promo-cream px-6 pb-8 pt-24 sm:pt-28 lg:order-none lg:items-center lg:px-10 lg:pb-10">
          <img
            src={promo.image}
            alt={promo.imageAlt}
            loading="lazy"
            className="w-full max-w-xl object-contain lg:mt-52"
          />
          {(promo.stickerLabel || promo.stickerValue || promo.stickerNote) && (
            <PromoSticker
              label={promo.stickerLabel}
              value={promo.stickerValue}
              note={promo.stickerNote}
              className="absolute right-5 top-5 sm:right-10 sm:top-8 lg:right-12 lg:top-12"
            />
          )}
        </div>
      </div>
    </section>
  );
}

/**
 * The red "Termasuk / Rp X / Perlengkapan" sticker: small, big, small, tilted,
 * with its top-left corner peeling.
 */
function PromoSticker({
  label,
  value,
  note,
  className,
}: {
  label: string;
  value: string;
  note: string;
  className?: string;
}) {
  const small = "relative whitespace-nowrap text-[11px] font-medium uppercase tracking-wide sm:text-sm lg:text-lg";
  return (
    <div
      className={cn(
        "relative flex size-32 -rotate-[30deg] flex-col items-center justify-center gap-0.5 overflow-hidden rounded-full bg-[#f04b3f] text-center text-white shadow-lg sm:size-40 lg:size-56",
        className,
      )}
    >
      {/* The peel: a darker flap folded over the edge. */}
      <span aria-hidden className="absolute -left-[10%] -top-[10%] size-[44%] rounded-br-full bg-[#8a1c14]" />
      {label && <span className={small}>{label}</span>}
      {value && (
        <span className="relative whitespace-nowrap text-xl font-medium leading-tight sm:text-3xl lg:text-4xl">{value}</span>
      )}
      {note && <span className={small}>{note}</span>}
    </div>
  );
}
